import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/ImageUpload";
import { Building2, Plus, Pencil, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const empty = {
  name: "", company_number: "", registered_office: "", trading_address: "",
  phone: "", email: "", website: "", logo: null, watermark: null, signature_image: null,
  default_signatory_name: "", signatory_title: "Director",
  default_dividend_frequency: "quarterly", dividend_cycle_anchor_month: 2,
  financial_year_end: "", share_classes: ["Ordinary"], allow_fractional_shares: false,
  brand_primary_color: "#0B1A2F", brand_accent_color: "#2EA3FD",
  voucher_safe_keeping_text: "This voucher should be kept in a safe place.",
  voucher_tax_responsibility_text: "It is your responsibility to declare any tax payable on dividends paid to you.",
};

export default function Companies() {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [classInput, setClassInput] = useState("");

  const load = () => api.get("/companies").then((res) => setCompanies(res.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };

  const addClass = () => {
    const v = classInput.trim();
    if (v && !form.share_classes.includes(v)) set("share_classes", [...form.share_classes, v]);
    setClassInput("");
  };
  const removeClass = (c) => set("share_classes", form.share_classes.filter((x) => x !== c));

  const save = async () => {
    if (!form.name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/companies/${editId}`, form);
      else await api.post("/companies", form);
      toast.success("Company saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="companies-page">
      <PageHeader title="Companies" description="Manage companies issuing dividends.">
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-company-button" className="bg-cass-blue text-white hover:bg-cass-bluehover">
            <Plus className="mr-2 h-4 w-4" /> Add Company
          </Button>
        )}
      </PageHeader>

      <Card className="border-cass-border shadow-sm">
        {companies === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div>
        ) : companies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="mb-3 h-10 w-10 text-cass-navy/20" />
            <p className="text-sm text-cass-navy/50">No companies yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-cass-surface hover:bg-cass-surface">
                <TableHead className="text-xs uppercase tracking-wider">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Number</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Share classes</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Signatory</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} className="hover:bg-cass-surface" data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-medium text-cass-navy">{c.name}</TableCell>
                  <TableCell className="text-cass-navy/70">{c.company_number || "—"}</TableCell>
                  <TableCell className="text-cass-navy/70">{(c.share_classes || []).join(", ")}</TableCell>
                  <TableCell className="text-cass-navy/70">{c.default_signatory_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-head text-cass-navy">{editId ? "Edit Company" : "Add Company"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Company name *"><Input data-testid="company-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Company number"><Input data-testid="company-number" value={form.company_number} onChange={(e) => set("company_number", e.target.value)} /></Field>
            <Field label="Registered office address" full><Textarea value={form.registered_office} onChange={(e) => set("registered_office", e.target.value)} /></Field>
            <Field label="Trading address (if different)" full><Textarea value={form.trading_address} onChange={(e) => set("trading_address", e.target.value)} /></Field>
            <Field label="Telephone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => set("website", e.target.value)} /></Field>
            <Field label="Financial year-end"><Input placeholder="e.g. 31 March" value={form.financial_year_end} onChange={(e) => set("financial_year_end", e.target.value)} /></Field>
            <Field label="Default signatory name"><Input value={form.default_signatory_name} onChange={(e) => set("default_signatory_name", e.target.value)} /></Field>
            <Field label="Signatory job title"><Input value={form.signatory_title} onChange={(e) => set("signatory_title", e.target.value)} /></Field>

            <Field label="Dividend frequency">
              <Select value={form.default_dividend_frequency} onValueChange={(v) => set("default_dividend_frequency", v)}>
                <SelectTrigger data-testid="dividend-frequency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="quarterly">Quarterly</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Quarter cycle start month">
              <Select value={String(form.dividend_cycle_anchor_month)} onValueChange={(v) => set("dividend_cycle_anchor_month", parseInt(v))}>
                <SelectTrigger data-testid="cycle-anchor"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Share classes" full>
              <div className="flex flex-wrap gap-2">
                {form.share_classes.map((c) => (
                  <span key={c} className="flex items-center gap-1 rounded-md bg-cass-surface px-2 py-1 text-sm text-cass-navy border border-cass-border">
                    {c}
                    <button type="button" onClick={() => removeClass(c)}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input placeholder="Add share class" value={classInput} data-testid="share-class-input"
                  onChange={(e) => setClassInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addClass())} />
                <Button type="button" variant="outline" onClick={addClass} data-testid="add-share-class">Add</Button>
              </div>
            </Field>

            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={form.allow_fractional_shares} onCheckedChange={(v) => set("allow_fractional_shares", v)} data-testid="fractional-switch" />
              <span className="text-sm text-cass-navy/80">Permit fractional shares</span>
            </div>

            <Field label="Brand primary colour"><Input type="color" value={form.brand_primary_color} onChange={(e) => set("brand_primary_color", e.target.value)} className="h-10 w-full p-1" /></Field>
            <Field label="Brand accent colour"><Input type="color" value={form.brand_accent_color} onChange={(e) => set("brand_accent_color", e.target.value)} className="h-10 w-full p-1" /></Field>

            <div className="col-span-2 grid grid-cols-3 gap-4">
              <ImageUpload label="Company logo" value={form.logo} onChange={(v) => set("logo", v)} testId="upload-logo" />
              <ImageUpload label="Watermark (optional)" value={form.watermark} onChange={(v) => set("watermark", v)} testId="upload-watermark" />
              <ImageUpload label="Signature (optional)" value={form.signature_image} onChange={(v) => set("signature_image", v)} testId="upload-signature" />
            </div>

            <Field label="Voucher 'safe place' wording" full><Textarea value={form.voucher_safe_keeping_text} onChange={(e) => set("voucher_safe_keeping_text", e.target.value)} /></Field>
            <Field label="Voucher tax responsibility wording" full><Textarea value={form.voucher_tax_responsibility_text} onChange={(e) => set("voucher_tax_responsibility_text", e.target.value)} /></Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-company" className="bg-cass-blue text-white hover:bg-cass-bluehover">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-sm text-cass-navy/80">{label}</Label>
      {children}
    </div>
  );
}
